import {
  Flex,
  HelpBubble,
  HelpBubbleContent,
  HelpBubbleTrigger,
  MannerWarmth,
  Text,
} from "@daangn/carotene";
import { atoms } from "@daangn/carotene/css";
import type { Maybe } from "__generated__/jampot/alpha-kr";
import clsx from "clsx";
import { isNil } from "lodash";

import { buildAllPageParams } from "~/core";
import { Avatar } from "~/design-system/components";
import { avatarSizeMapper } from "~/design-system/components/Persona/Persona";
import { persona } from "~/design-system/components/Persona/style";
import { Link } from "~/remix-lib";

import { useLocation } from "@remix-run/react";
import { getService } from "~/utils/getService";
import DefaultUserAvatar from "../assets/default_user_avatar.png";
import * as css from "./UserProfileSummary.css";

const size = {
  base: "medium",
  small: "xlarge",
  medium: "large",
  large: "xlarge",
} as const;

const classNames = persona({ size });

type Props = {
  userProfile?: Maybe<{
    id: string;
    nickname: string;
    profileImage: string;
    region?: {
      dbId: string;
      name: string;
    };
    score?: number;
  }>;
};

/**
 * @description 한국 유저 프로필
 */
export function UserProfileSummary({ userProfile: author }: Props) {
  const location = useLocation();
  const service = getService(location.pathname);

  if (isNil(author)) {
    return <WithdrawnUserProfile />;
  }

  return (
    <Flex flexDirection="column" paddingY={4}>
      <Flex justifyContent="spaceBetween" alignItems="center">
        <div className={classNames.root}>
          <Link
            href={author.id}
            aria-label={`${author.nickname}님의 프로필 페이지`}
            className={clsx(css.link, css.avatarLinkFocusVisible)}
          >
            <Avatar
              size={avatarSizeMapper(size)}
              src={author.profileImage}
              alt={`${author.nickname}님의 프로필`}
              aria-hidden
            />
          </Link>
          <div className={classNames.content}>
            <Link href={author.id} className={css.link}>
              <span className={classNames.primaryText}>{author.nickname}</span>
            </Link>
            {author.region?.name && author.region.dbId && (
              <Link
                href={`/kr/${service}/?${buildAllPageParams({
                  region: {
                    id: author.region.dbId,
                    name: author.region.name,
                  },
                })}`}
                className={classNames.secondaryText}
              >
                {author.region.name}
              </Link>
            )}
          </div>
        </div>
        {author.score ? (
          <Flex
            flexDirection="column"
            gap={{
              base: 1,
              small: 2,
              medium: 1,
              large: 2,
            }}
            alignItems="flexEnd"
          >
            <MannerWarmth
              temperature={author.score}
              size={{
                base: "medium",
                small: "large",
                medium: "medium",
                large: "large",
              }}
            />
            <HelpBubble>
              <HelpBubbleTrigger asChild>
                <Text
                  size={{
                    base: "small",
                    small: "medium",
                    medium: "small",
                    large: "medium",
                  }}
                  color="neutralSubtle"
                  className={css.mannerWarmthText}
                  asChild
                >
                  <button type="button">매너온도</button>
                </Text>
              </HelpBubbleTrigger>
              <HelpBubbleContent align="end" className={css.bubbleContent}>
                매너온도는 당근 사용자로부터 받은 칭찬, 후기, 비매너 평가,
                운영자 제재 등을 종합해서 만든 매너 지표예요.
              </HelpBubbleContent>
            </HelpBubble>
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  );
}

const WithdrawnUserProfile = () => {
  return (
    <Flex flexDirection="column" paddingY={4}>
      <Flex justifyContent="spaceBetween" alignItems="center">
        <div className={classNames.root}>
          <Avatar
            size={avatarSizeMapper(size)}
            src={DefaultUserAvatar}
            alt=""
          />
          <div className={classNames.content}>
            <div className={classNames.primaryText}>
              <span
                className={atoms({
                  color: "placeholder",
                })}
              >
                탈퇴 사용자
              </span>
            </div>
          </div>
        </div>
      </Flex>
    </Flex>
  );
};
